import { useEffect, useState } from "react";
import { PiggyBank, TrendingUp, TrendingDown } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const fmtDur = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;

export default function RentabilitePanel({ project: p, onChanged }) {
  const [data, setData] = useState(null);
  const [rate, setRate] = useState("");
  const load = () => api.get(`/graphisme-planning/projects/${p.id}/planning`)
    .then((r) => { setData(r.data); setRate(String(r.data.rentabilite?.internal_hourly_rate || "")); })
    .catch(() => {});
  useEffect(() => { load(); }, [p.id, p.price_ht, p.estimated_minutes]); // eslint-disable-line
  if (!data) return <p className="py-8 text-center text-xs text-neutral-400">Chargement…</p>;
  const r = data.rentabilite;
  const saveRate = async () => {
    try {
      await api.patch(`/graphisme/projects/${p.id}`, { internal_hourly_rate: +rate || 0 });
      toast.success("Taux horaire interne mis à jour"); onChanged(); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const rows = [
    ["Montant facturé HT", r.price_ht > 0 ? eur(r.price_ht) : "—"],
    ["Temps estimé", data.estimated_minutes ? fmtDur(data.estimated_minutes) : "—"],
    ["Temps réservé (agenda)", data.reserved_minutes ? fmtDur(data.reserved_minutes) : "—"],
    ["Temps réellement consommé", data.real_minutes ? fmtDur(data.real_minutes) : "—"],
    ["Coût horaire interne", `${eur(r.internal_hourly_rate)}/h`],
    ["Coût de production estimé", eur(r.cout_estime)],
    ["Coût réel de production", eur(r.cout_reel)],
  ];
  return (
    <div className="space-y-4" data-testid="rentabilite-panel">
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><PiggyBank className="w-4 h-4 text-emerald-600" /> Rentabilité du projet</h3>
        <p className="text-[11px] text-neutral-400 mb-4">Indicateur de rentabilité interne — à ne pas confondre avec le bénéfice comptable net.</p>
        <div className="space-y-1.5 text-sm">
          {rows.map(([l, v]) => (
            <p key={l} className="flex justify-between border-b border-neutral-50 pb-1.5"><span className="text-neutral-500">{l}</span><strong>{v}</strong></p>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-2.5 mt-4">
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3" data-testid="renta-marge-prev">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400">Marge prévisionnelle</p>
            <p className={`font-heading font-bold text-lg mt-0.5 ${r.marge_previsionnelle >= 0 ? "text-emerald-700" : "text-red-600"}`}>{eur(r.marge_previsionnelle)}</p>
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3" data-testid="renta-marge-reelle">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400">Marge réelle</p>
            <p className={`font-heading font-bold text-lg mt-0.5 ${r.marge_reelle >= 0 ? "text-emerald-700" : "text-red-600"}`}>{eur(r.marge_reelle)}</p>
          </div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3" data-testid="renta-taux">
            <p className="text-[10px] uppercase tracking-wider text-neutral-400">Taux de marge (prix de vente)</p>
            <p className={`font-heading font-bold text-lg mt-0.5 flex items-center gap-1 ${r.taux_marge_reel == null ? "text-neutral-400" : r.taux_marge_reel >= 0 ? "text-emerald-700" : "text-red-600"}`}>
              {r.taux_marge_reel != null && (r.taux_marge_reel >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />)}
              {r.taux_marge_reel != null ? `${r.taux_marge_reel} %` : "—"}
            </p>
          </div>
        </div>
        {r.ecart_minutes != null && (
          <p className={`text-xs mt-3 rounded-lg px-3 py-2 border ${r.ecart_minutes > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`} data-testid="renta-ecart">
            {r.ecart_minutes > 0
              ? `Dépassement : +${fmtDur(r.ecart_minutes)} par rapport à l'estimation.`
              : `En avance : ${fmtDur(-r.ecart_minutes)} de moins que l'estimation.`}
          </p>
        )}
      </div>
      <div className="bg-white border border-neutral-200 rounded-xl p-5">
        <h4 className="font-heading font-bold text-sm mb-2">Taux horaire interne du projet</h4>
        <p className="text-[11px] text-neutral-400 mb-2">Sert au calcul de rentabilité — n'impose pas le prix de vente. Laisser à la valeur agence par défaut si 0.</p>
        <div className="flex gap-2">
          <Input type="number" min="0" value={rate} onChange={(e) => setRate(e.target.value)} className="w-32" data-testid="renta-rate-input" />
          <Button variant="outline" size="sm" onClick={saveRate} data-testid="renta-rate-save">Enregistrer</Button>
        </div>
      </div>
    </div>
  );
}
